import React, { useContext, useEffect, useState, useRef } from 'react';
import { Button, Collapse, OverlayTrigger, Modal, Tooltip, Form } from 'react-bootstrap';
import { withTranslation } from 'react-i18next';
import { BiChevronDown, BiPencil, BiTrash, BiPlus, BiX } from 'react-icons/bi';
import { LoaderContext, RegulationContext } from "../../../App";
import { BsPlusCircle, BsEye } from "react-icons/bs";
import './EnterpriseStructure.scss';
import { FcFinePrint } from "react-icons/fc";
import { toast } from 'react-toastify';
import esg_ai from '../../../images/esg_ai.png';
import ConfirmationModal from "../../../components/ConfirmationModal/ConfirmationModal";
import { LS_AUTHORITY_KEY, LS_CONTENT_BLOCKS_FILTER_KEY } from '../../../core/constants/common';
import ContentBlockService from '../../../core/service/ContentBlockService';
import HTMLEditor from '../../../components/Editor/HTMLEditor';
import no_photo from '../../../images/no-photo.jpg';
import ReportingPeriod from '../../../components/Regulation/ReportingPeriod';
import { useSearchParams } from 'react-router-dom';
import AIContentExtract from '../../../components/AI/AIContentExtract';
import { CgSortAz } from "react-icons/cg";
import AppFilter from '../../../components/AppFilter/AppFilter';
import ContentBlocksFilterStatus from './ContentBlocksFilterStatus';

const ContentBlocks = ({ t, showAIWidgetScreen, closeWidget }) => {

    const lsData = JSON.parse(localStorage.getItem(LS_AUTHORITY_KEY));
    const { glbRegulation } = useContext(RegulationContext);
    const { setLoaderDisplay } = useContext(LoaderContext);
    const [contentBlocks, setContentBlocks] = useState([]);
    const [contentBlocksDetails, setContentBlocksDetails] = useState([]);
    const [cbDisplay, setCbDisplay] = useState({});
    const [showAddSection, setShowAddSection] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [titleName, setTitleName] = useState('');
    const [about, setAbout] = useState('');
    const [description, setDescription] = useState('');
    const [validated, setValidated] = useState(false);
    const [selectedData, setSelectedData] = useState({});
    const [showConfirmation, setShowConfirmation] = useState(false);
    let messageConfirmation = t("ContentBlockModal.label");
    const addRef = useRef(null);
    const listRef = useRef(null);
    const editorRef = useRef(null);
    const [filterDisplay, setFilterDisplay] = useState(false);
    const year = new Date().getFullYear();
    const [searchParams] = useSearchParams();
    // Utility functions for local storage operations
    const saveFilterToStorage = (filterData) => {
        try {
            localStorage.setItem(LS_CONTENT_BLOCKS_FILTER_KEY, JSON.stringify(filterData));
        } catch (error) {
            console.warn('Failed to save content blocks filter to local storage:', error);
        }
    };

    const getFilterFromStorage = () => {
        try {
            const savedFilter = localStorage.getItem(LS_CONTENT_BLOCKS_FILTER_KEY);
            return savedFilter ? JSON.parse(savedFilter) : null;
        } catch (error) {
            console.warn('Failed to parse saved content blocks filter:', error);
            return null;
        }
    };

    // Initialize filter with local storage or default values
    const getInitialFilter = () => {
        const urlPeriod = searchParams.get('period');
        
        if (urlPeriod) {
            return { reportingPeriod: urlPeriod };
        } else {
            const savedFilter = getFilterFromStorage();
            return savedFilter || { reportingPeriod: `${year}-01-01` };
        }
    };

    const [filter, setFilter] = useState(getInitialFilter());
    const [showModal, setShowModal] = useState(false);
    const [showAIContentExtractWidget, setShowAIContentExtractWidget] = useState(false);
    const [extractedContent, setExtractedContent] = useState('');
    const [contentBlockTopics, setContentBlockTopics] = useState([]);
    const [selTopic, setSelTopic] = useState(0);

    useEffect(() => {
        if (!glbRegulation.id) return;
        resetForm();
        getContentBlocks(glbRegulation.id, filter.reportingPeriod);
        getContentBlockTopics(glbRegulation.id);
    }, [glbRegulation.id, filter.reportingPeriod]);

    useEffect(() => {
        if (!extractedContent) return;
        setDescription(description + '\n' + extractedContent);
    }, [extractedContent]);

    const getContentBlocks = (standard, period) => {
        setLoaderDisplay(true);
        ContentBlockService.getContentBlocks(standard, period)
            .then(res => {
                setLoaderDisplay(false);
                setContentBlocks(res.data);
            })
            .catch((error) => {
                setLoaderDisplay(false);
            });
    };

    const getContentBlockTopics = (standard) => {
        setLoaderDisplay(true);
        ContentBlockService.getContentBlockTopics(standard)
            .then(res => {
                setLoaderDisplay(false);
                setContentBlockTopics(res.data);
            })
            .catch((error) => {
                setLoaderDisplay(false);
            });
    };

    const renderEmptyDataLabel = () => {
        return (
            <div className="Nodatafound_box">
                <h6><FcFinePrint className="me-2" />{t("NoContentBlocks.label")}</h6>
            </div>
        )
    };

    const getContentBlockDetails = (id, mode) => {
        setLoaderDisplay(true);
        ContentBlockService.getContentBlockDetails(id)
            .then(res => {
                setLoaderDisplay(false);
                setContentBlocksDetails(res.data);
                const existData = contentBlocks;
                const index = existData.findIndex(obj => obj.id === id);
                if (index !== -1) {
                    existData.splice(index, 1, res.data);
                }
                setContentBlocks(existData);
                if (mode == 'edit') {
                    setSelectedData(res.data);
                    setTitleName(res.data.name);
                    setAbout(res.data.about);
                    setDescription(res.data.description);
                    setSelTopic(res.data.topic.id ?? null)
                } else if (mode == 'view') {
                    setShowModal(true);
                }
            })
            .catch((error) => {
                setLoaderDisplay(false);
            });
    }

    const createContentBlock = (passData) => {
        setLoaderDisplay(true);
        ContentBlockService.createContentBlock(passData)
            .then(res => {
                setLoaderDisplay(false);
                toast.success("Saved successfully", {
                    position: toast.POSITION.TOP_RIGHT,
                });
                setShowAddSection(false);
                let existData = contentBlocks;
                existData.push(res.data);
                setContentBlocks(existData);
                resetForm();
                setShowAddSection(false);
            })
            .catch((error) => {
                setLoaderDisplay(false);
            });
    }

    const updateContentBlock = (passData, id) => {
        setLoaderDisplay(true);
        ContentBlockService.updateContentBlock(passData, id)
            .then(res => {
                setLoaderDisplay(false);
                toast.success("Updated successfully", {
                    position: toast.POSITION.TOP_RIGHT,
                });
                resetForm();
                setShowAddSection(false);
                getContentBlocks(glbRegulation.id, filter.reportingPeriod);
            })
            .catch((error) => {
                setLoaderDisplay(false);
            });
    }

    const deleteContentBlock = (id) => {
        setLoaderDisplay(true);
        ContentBlockService.deleteContentBlock(id)
            .then(res => {
                setLoaderDisplay(false);
                toast.success("Deleted successfully", {
                    position: toast.POSITION.TOP_RIGHT,
                });
                resetForm();
                setShowAddSection(false);
                getContentBlocks(glbRegulation.id, filter.reportingPeriod);
            })
            .catch((error) => {
                setLoaderDisplay(false);
            });
    }

    // const transferTextToEditor = () => {
    //     if (extractedContent) {
    //         setDescription(description + '\n' + extractedContent);
    //     }
    // };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setValidated(true);

        const form = e.currentTarget;
        if (form.checkValidity() === false || editorRef.current.getContent() == '') {
            e.stopPropagation();
            toast.warning('Please fill all the mandatory fields');
        } else {
            let passData = {
                regulationId: glbRegulation.id,
                period: filter.reportingPeriod,
                name: titleName,
                type: "QUALITATIVE",
                about: about,
                description: editorRef?.current?.getContent(),
                topic: selTopic ? { id: selTopic } : null
            }
            if (editMode) {
                updateContentBlock(passData, selectedData.id);
            } else {
                createContentBlock(passData);
            }
        }
    }

    const resetForm = () => {
        setShowAddSection(false);
        setSelectedData(null);
        setEditMode(false);
        setTitleName('');
        setAbout('');
        setDescription('');
        setValidated(false);
        scrollToAddSection();
        setShowAIContentExtractWidget(false);
        setSelTopic(0);
    }

    const confirmDelete = (val) => {
        setShowConfirmation(false);
        if (val) {
            deleteContentBlock(selectedData.id);
        }
    };

    const showConfirm = (data) => {
        setSelectedData(data)
        setShowConfirmation(true);
    }

    const setupEdit = (data) => {
        setShowAddSection(true);
        setEditMode(true);
        getContentBlockDetails(data.id, 'edit');
        // scrollToAddSection();
    }

    const scrollToAddSection = () => {
        addRef.current?.scrollIntoView({ behavior: "smooth" });
    };

    const scrollToListSection = () => {
        listRef.current?.scrollIntoView({ behavior: "smooth" });
    };

    const showExtractContent = () => {
        setShowAIContentExtractWidget(true);
        // Call parent component's showAIWidgetScreen if provided
        if (showAIWidgetScreen) {
            showAIWidgetScreen(true);
        }
    }

    const renderFilter = () => {
        return (
            <div className={filterDisplay ? '' : 'd-none'}>
                <AppFilter
                    type={'NR_CONTENTBLOCKS'}
                    existingFilter={{ period: filter.reportingPeriod }}
                    onApplyFilter={(f) => { 
                        setReportingPeriod(f.period); 
                        getContentBlocks(glbRegulation.id, f.period); 
                    }}
                    onFilterHide={(value) => setFilterDisplay(value)} />
            </div>
        )
    };

    const setReportingPeriod = (period) => {
        const [year, month] = period.split('-');
        const f = { ...filter, reportingPeriod: `${year}-01-01` };
        setFilter(f);
        
        // Save filter to local storage for persistence
        saveFilterToStorage(f);
    };

    const hideAIContentExtract = () => {
        setShowAIContentExtractWidget(false);
        // Call parent component's closeWidget if provided
        if (closeWidget) {
            closeWidget(false);
        }
    }

    return (
        <div>
            <div className="d-flex align-items-center justify-content-end mb-2">
                <ContentBlocksFilterStatus 
                    filter={filter}
                    onFilterClick={() => setFilterDisplay(!filterDisplay)}
                />
            </div>
            {renderFilter()}
            <div className='row m-0'>
                {/* {contentBlocks.length === 0 && renderEmptyDataLabel()} */}
                <div className='contentBlocks_head m-2'>
                    <div className='contentBlocks_head_in'>{t("CBNewButton.label")}</div>
                    <Button variant='primary' onClick={() => { setShowAddSection(true); scrollToAddSection() }}>
                        <BsPlusCircle className=' me-2' />
                        {t("New.label")}
                    </Button>
                </div>
                {/* <div ref={addRef} /> */}

                {showAddSection && (
                    <div className={`${showAIContentExtractWidget ? 'col-md-8 p-2' : 'col-md-12 p-2'}`}>
                        <div className="card mb-2">
                            <Form noValidate validated={validated} onSubmit={handleSubmit}>
                                <div className='General_topics_Add'>
                                    <div className='General_topics_Add_head'>
                                        <label>{t("Title.label")} <span className="text-danger">*</span></label>
                                        <input className='form-control' value={titleName} required placeholder={t("AddTitle.label")}
                                            onChange={(e) => setTitleName(e.target.value)} maxLength={150} />
                                        <div className='text-right'>  {titleName.length + '/150 ' + t("CharactersRemaining.label")}</div>
                                    </div>
                                    <div className='General_topics_Content'>
                                        <label>{t("Topics.label")} <span className="text-danger">*</span></label>
                                        <select className='form-select' onChange={(e) => setSelTopic(e.target.value)} value={selTopic}>
                                            <option value='' key=''>Select</option>
                                            {
                                                contentBlockTopics?.map((topic, index) => {
                                                    return <option value={topic.id} key={`topic-${index}`}>{topic.name}</option>;
                                                })
                                            }
                                        </select>
                                    </div>
                                    <div className='General_topics_Content'>
                                        <div className='mb-2'>
                                            <label>{t("About.label")} <span className="text-danger">*</span></label>
                                            <textarea className="form-control" rows={3} value={about} maxLength={250} required
                                                onChange={(e) => setAbout(e.target.value)} placeholder={t("AddAbout.label")}></textarea>
                                            <div className='text-right'>  {about.length + '/250 ' + t("CharactersRemaining.label")}</div>
                                        </div>
                                        <div>
                                            <label>{t("Description.label")} <span className="text-danger">*</span></label>
                                            {/* <textarea className="form-control" rows={10} value={description} required
                                                    onChange={(e) => setDescription(e.target.value)}></textarea> */}
                                            <div id="desc">
                                                <HTMLEditor editorRef={editorRef} initialValue={description} selectorVal={'#desc'} />
                                            </div>
                                        </div>
                                        <div className='text-left py-1'>
                                            <OverlayTrigger
                                                placement="top"
                                                overlay={<Tooltip id="advanced">{t("ExtractContentTooltip.label")}</Tooltip>}>
                                                <Button variant='secondary' className="btn btn-ESGAi mx-2"
                                                    onClick={() => showExtractContent()}>
                                                    <img src={esg_ai} width={15} height={15} alt="ESG AI" />
                                                    {t("ExtractWithESGAI.label")}
                                                </Button>
                                            </OverlayTrigger>
                                            <button type="submit" className="btn btn-primary me-2">{editMode ? t("Update.label") : t("Save.label")}</button>
                                            <button type="button" className="btn btn-secondary" onClick={() => { setShowAddSection(false); resetForm() }}>{t("Close.label")}</button>
                                        </div>
                                    </div>
                                </div>
                            </Form>
                        </div>
                    </div>
                )}

                {
                    showAIContentExtractWidget &&
                    <div className='col-md-4 p-2'>
                        <AIContentExtract onHideWidget={() => hideAIContentExtract()} transferContent={(content) => setExtractedContent(content)} />
                    </div>
                }


                {!showAddSection &&
                    <>
                        {
                            contentBlocks?.map((items) => {
                                return (
                                    <div className='col-lg-3 col-md-4 p-2' key={items.id}>
                                        <div className="card mb-0 h-100">
                                            <div className='General_topics_title_img'>
                                                <img src={no_photo} alt="no_photo" />
                                            </div>
                                            <div className="General_topics_title">
                                                <h6 className="mb-0">
                                                    {items.name}
                                                </h6>
                                                <small>{items.about}</small>
                                                <div className='btnContentBlocks'>
                                                    <Button className="btn px-2 border"
                                                        variant="blank_outline"
                                                        onClick={() => {
                                                            getContentBlockDetails(items.id, 'view');
                                                        }}>
                                                        <BsEye />
                                                    </Button>
                                                    <Button className="btn px-2 border"
                                                        variant="blank_outline"
                                                        onClick={() => setupEdit(items)}>
                                                        <BiPencil />
                                                    </Button>
                                                    <Button className="btn px-2 border"
                                                        variant="blank_outline"
                                                        onClick={() => showConfirm(items)}>
                                                        <BiTrash />
                                                    </Button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })
                        }
                    </>
                }

                <Modal show={showModal} onHide={() => setShowModal(false)}
                    size="fullscreen"
                >
                    <Modal.Header closeButton>
                        <Modal.Title as="h5">{contentBlocksDetails?.name}</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <div className='General_topics_ContentShow'>
                            <div dangerouslySetInnerHTML={{ __html: contentBlocksDetails?.description }}></div>
                        </div>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={() => setShowModal(false)}>
                            {t("Close.label")}
                        </Button>
                    </Modal.Footer>
                </Modal>

                {showConfirmation && (
                    <ConfirmationModal
                        modalShow={true}
                        messageText={messageConfirmation}
                        callbackModal={confirmDelete}
                        confirmBtn={t("Confirm.label")}
                        CancelBtn={t("Cancel.label")}
                    />
                )}

            </div>
        </div>
    )

}

export default withTranslation()(ContentBlocks);
